import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, filter, finalize, fromEvent, of, Subscription } from 'rxjs';
import { BaseService } from '../../../services/base.service';
import { TaskService } from '../../../services/task.service';
import { Task, TaskUser } from '../tasks.types';
import { getEndOfDay, isSameDay } from '../tasks.utils';

/** A tab that comes back after this long reads the day again: the page can stay open overnight */
const REFRESH_AFTER_MS = 60_000;

interface TodayGroup {
  key: 'overdue' | 'today';
  tasks: Task[];
}

/**
 * The tasks of the current user for today, for the home page: what is overdue and what is due before the day ends,
 * and the tasks they created that wait for their review. A task opens in the sidebar over the page, and a new one
 * starts with the end of today as its deadline, so it shows up here.
 */
@Component({
  selector: 'app-tasks-today',
  templateUrl: './tasks-today.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block' },
})
export class TasksTodayComponent implements OnInit {
  private readonly taskService = inject(TaskService);
  private readonly baseService = inject(BaseService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly tasksUrl = this.baseService.routerSettings('/tasks');
  protected readonly currentUserId = this.taskService.currentUserId;
  protected readonly isSameDay = isSameDay;

  /** The overdue tasks first, then the ones that are due today: the groups that have no task are not shown */
  protected groups: TodayGroup[] = [];
  protected shown = 0;
  protected total = 0;
  protected loading = true;
  protected failed = false;

  private usersById = new Map<string, TaskUser>();
  private request?: Subscription;
  private loadedAt = 0;

  ngOnInit(): void {
    this.taskService
      .getUsers()
      .pipe(
        catchError(() => of([] as TaskUser[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((users) => (this.usersById = new Map(users.map((user) => [user.id, user]))));

    this.load();
    fromEvent(document, 'visibilitychange')
      .pipe(
        filter(() => document.visibilityState === 'visible' && Date.now() - this.loadedAt > REFRESH_AFTER_MS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.load(false));
  }

  protected getUser(id: string): TaskUser | null {
    return this.usersById.get(id) ?? null;
  }

  /** Without the loader the list stays as it is while it is read again, and a failure of that is not shown */
  protected load(showLoader = true): void {
    this.request?.unsubscribe();
    this.loading = showLoader;
    this.failed = false;
    this.request = this.taskService
      .getTodayTasks()
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ items, total }) => {
          const overdue = items.filter((task) => task.is_overdue);
          const today = items.filter((task) => !task.is_overdue);
          this.groups = [
            { key: 'overdue' as const, tasks: overdue },
            { key: 'today' as const, tasks: today },
          ].filter((group) => group.tasks.length);
          this.shown = items.length;
          this.total = total;
          this.loadedAt = Date.now();
        },
        error: (error) => {
          if (!showLoader) return;

          this.failed = true;
          this.taskService.error(error);
        },
      });
  }

  /** The form opens with the end of today as the deadline: a task made here is a task of the day */
  protected create(): void {
    this.taskService
      .openTaskForm({ deadline: getEndOfDay() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((task) => task && this.load(false));
  }

  protected open(task: Task): void {
    this.taskService
      .openTaskDetail(task.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => result?.changed && this.load(false));
  }
}
